import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css'; 

const AdminPage = ({ showToast }) => {
  const [activeTab, setActiveTab] = useState('active_orders');
  
  const [orders, setOrders] = useState([]); 
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]); 
  const [reviews, setReviews] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  const [editId, setEditId] = useState(null);

  const [carouselType, setCarouselType] = useState('DESKTOP'); 
  const [bannerFiles, setBannerFiles] = useState([]); 

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null 
  });

  const [newProduct, setNewProduct] = useState({
    name: '', price: '', mrp: '', weight: '', 
    length: '', width: '', height: '',
    hsnCode: '', skuId: '', description: '', washcare: '', shippingPolicy: '', category: 'tshirt', productImage: '', sizeChart: '', 
    stock: { S: 0, M: 0, L: 0, XL: 0 } 
  });
  
  const [extraImages, setExtraImages] = useState([]);

  // 🟢 NEW STATES FOR RAW FILES
  const [mainImageFile, setMainImageFile] = useState(null);
  const [sizeChartFile, setSizeChartFile] = useState(null);
  const [extraImageFiles, setExtraImageFiles] = useState([]);

  // 🟢 HELPER: To display images properly (Blob previews or Server filenames)
  // 🟢 HELPER: To display images properly (Handles Base64, Arrays, and raw filenames)
  const getAdminImageUrl = (imgPath) => {
      let img = imgPath || "";
      if (!img) return "https://via.placeholder.com/100x100/2a201c/ffffff?text=AV";
      
      // JSON Array Handle karna (eg. ["abc.png"])
      if (typeof img === 'string' && img.startsWith("[")) {
          try { img = JSON.parse(img)[0]; } catch(e) { img = img.replace(/[\[\]"]/g, ''); }
      }
      
      // Final URL Generate karna
      if (typeof img === 'string' && !img.startsWith('http') && !img.startsWith('data:image') && !img.startsWith('blob:') && !img.startsWith('/assets')) {
          img = `https://antarvaani.com/api/uploads/${img.startsWith('/') ? img.substring(1) : img}`;
      }
      
      return img;
  };

  useEffect(() => {
    const footer = document.querySelector('.hybrid-footer');
    const mobileFooterTop = document.querySelector('.mobile-footer-top');
    const mobileFooterBottom = document.querySelector('.mobile-bottom-bar');

    if (footer) footer.style.display = 'none';
    if (mobileFooterTop) mobileFooterTop.style.display = 'none';
    if (mobileFooterBottom) mobileFooterBottom.style.display = 'none';

    return () => {
      if (footer) footer.style.display = 'block';
      if (mobileFooterTop) mobileFooterTop.style.display = 'block';
      if (mobileFooterBottom) mobileFooterBottom.style.display = 'flex';
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const ordersRes = await fetch("/api/orders/all"); 
      if (ordersRes.ok) setOrders(await ordersRes.json());

      const productsRes = await fetch("/api/products/all");
      if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData);
          
          let allReviews = [];
          for (let product of productsData) {
              try {
                  const reviewRes = await axios.get(`/api/reviews/product/${product.id}`); 
                  const productReviews = reviewRes.data.map(r => ({ ...r, productName: product.name, productImage: product.productImage }));
                  allReviews = [...allReviews, ...productReviews];
              } catch (err) {
                  console.error(`Error fetching reviews for product ${product.id}`);
              }
          }
          setReviews(allReviews);
      }

      const bannerRes = await fetch('/api/carousel/all');
      if (bannerRes.ok) setBanners(await bannerRes.json());

    } catch (error) {
      console.error("Error connecting to server:", error);
      showToast("❌ Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const triggerConfirm = (title, message, action) => {
      setConfirmModal({
          isOpen: true,
          title,
          message,
          onConfirm: async () => {
              await action();
              setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null }); 
          }
      });
  };

  const updateStatusApi = async (id, newStatus) => {
    try {
      const response = await fetch(`/api/orders/update/${id}`, {
        method: 'PUT',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updatedOrders = orders.map(o => o.id === id ? { ...o, status: newStatus } : o);
        setOrders(updatedOrders);
        return true;
      }
      return false;
    } catch (error) {
      showToast("❌ Error connecting to backend.");
      return false;
    }
  };

  const handleArchive = (order) => {
    triggerConfirm("Archive Order?", "This will move order to history.", async () => {
        const smartStatus = `Archived_${order.status}`; 
        const success = await updateStatusApi(order.id, smartStatus);
        if (success) showToast("✅ Order Moved to Archive! 🗄️");
    });
  };

  const handleRestore = (order) => {
    triggerConfirm("Restore Order?", "This will move order back to active list.", async () => {
        const originalStatus = order.status.replace('Archived_', '');
        const success = await updateStatusApi(order.id, originalStatus);
        if (success) showToast("✅ Order Restored! ↺");
    });
  };

  const handleDeleteOrder = (id) => {
    triggerConfirm("Delete Order Permanently?", "This cannot be undone.", async () => {
        try {
            const response = await fetch(`/api/orders/delete/${id}`, { method: 'DELETE' });
            if (response.ok) {
                setOrders(orders.filter(o => o.id !== id)); 
                showToast("🗑️ Order Deleted Permanently");
            } else {
                showToast("❌ Failed to delete order.");
            }
        } catch (error) { showToast("⚠️ Server Error."); }
    });
  };

  const handleStatusChange = (id, newStatus) => {
    triggerConfirm("Update Status?", `Change status to "${newStatus}"?`, async () => {
        const success = await updateStatusApi(id, newStatus);
        if (success) showToast(`✅ Order #${id} Updated`);
    });
  };

  const handleBannerUploadSubmit = async () => {
      if (bannerFiles.length === 0) {
          showToast("⚠️ Please select an image first!"); return;
      }

      const currentCount = banners.filter(b => b.type === carouselType).length;
      const maxLimit = carouselType === 'DESKTOP' ? 3 : 4;

      if (currentCount + bannerFiles.length > maxLimit) {
          showToast(`⚠️ You can only add ${maxLimit - currentCount} more ${carouselType} image(s).`);
          return;
      }

      for (let file of bannerFiles) {
          const compressedBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = (event) => {
                  const img = new Image();
                  img.src = event.target.result;
                  img.onload = () => {
                      const canvas = document.createElement('canvas');
                      const MAX_WIDTH = carouselType === 'DESKTOP' ? 1400 : 800; 
                      let scaleSize = 1;
                      
                      if (img.width > MAX_WIDTH) {
                          scaleSize = MAX_WIDTH / img.width;
                      }
                      
                      canvas.width = img.width * scaleSize;
                      canvas.height = img.height * scaleSize;

                      const ctx = canvas.getContext('2d');
                      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                      const newDataUrl = canvas.toDataURL('image/webp', 0.7);
                      resolve(newDataUrl);
                  };
              };
          });

          try {
              const res = await fetch('/api/carousel/add', {
                  method: 'POST',
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ imageUrl: compressedBase64, type: carouselType }) 
              });
              if(res.ok) {
                  showToast(`✅ ${carouselType} Banner Uploaded!`);
              } else {
                  showToast("❌ Failed to add banner");
              }
          } catch(err) {
              showToast("❌ Server Error");
          }
      }
      
      fetchData(); 
      setBannerFiles([]); 
      if(document.getElementById("bannerInput")) document.getElementById("bannerInput").value = "";
  };

  const deleteBanner = (id) => {
      triggerConfirm("Delete Banner?", "It will be removed from homepage.", async () => {
          try {
              await fetch(`/api/carousel/delete/${id}`, { method: 'DELETE' });
              setBanners(banners.filter(b => b.id !== id));
              showToast("🗑️ Banner Deleted");
          } catch(err) { showToast("❌ Error deleting banner"); }
      });
  };

  const handleDeleteReview = (reviewId) => {
    triggerConfirm("Delete Review?", "This action cannot be undone.", async () => {
        try {
            const res = await axios.delete(`/api/reviews/delete/${reviewId}`);
            if (res.status === 200) {
                setReviews(reviews.filter(r => r.id !== reviewId));
                showToast("✅ Review Deleted Successfully");
            }
        } catch (err) {
            console.error(err);
            showToast("❌ Failed to delete review");
        }
    });
  };

  const getReturnOrders = () => orders.filter(o => o.status.includes('Return') || o.status.includes('Exchange'));
  const getActiveOrders = () => orders.filter(o => !o.status.includes('Return') && !o.status.includes('Exchange') && !o.status.includes('Archived'));
  const getArchivedOrders = () => orders.filter(o => o.status.includes('Archived'));
  
  const getDisplayStatus = (statusString) => {
      if(!statusString) return "";
      return statusString.replace('Archived_', '');
  };

  const handleEditClick = (product) => {
      let parsedStock = { S: 0, M: 0, L: 0, XL: 0 };
      try { parsedStock = JSON.parse(product.stock); } catch(e) {}

      setNewProduct({
          name: product.name,
          price: product.price,
          mrp: product.mrp,
          weight: product.weight || '', 
          length: product.length || '', 
          width: product.width || '',  
          height: product.height || '', 
          hsnCode: product.hsnCode || '', 
          skuId: product.skuId || '',     
          category: product.category,
          description: product.description || '',
          washcare: product.washcare || '', 
          shippingPolicy: product.shippingPolicy || '', 
          productImage: product.productImage, // Retains existing filename
          sizeChart: product.sizeChart || '', // Retains existing filename
          stock: parsedStock
      });

      setExtraImages(product.images || []);
      setEditId(product.id);
      
      // Clear file states in case of lingering previous edits
      setMainImageFile(null);
      setSizeChartFile(null);
      setExtraImageFiles([]);

      setActiveTab('add');
      window.scrollTo(0,0);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    showToast("⏳ Saving Product... Please wait.");

    // 🟢 HELPER TO UPLOAD FILE
    const uploadFileToServer = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/products/upload", {
            method: "POST",
            body: formData
        });
        if (res.ok) {
            return await res.text(); // returns the unique filename
        }
        throw new Error("Failed to upload file");
    };

    try {
        let finalMainImage = newProduct.productImage;
        let finalSizeChart = newProduct.sizeChart;
        let finalExtraImages = [...extraImages];

        // Only upload if a NEW file was selected
        if (mainImageFile) {
            finalMainImage = await uploadFileToServer(mainImageFile);
        }
        if (sizeChartFile) {
            finalSizeChart = await uploadFileToServer(sizeChartFile);
        }
        if (extraImageFiles.length > 0) {
            const uploadedExtras = await Promise.all(extraImageFiles.map(f => uploadFileToServer(f)));
            finalExtraImages = uploadedExtras; // Replaces previous gallery images. You can adjust this to append if needed.
        }

        const productData = {
            name: newProduct.name, price: newProduct.price, mrp: newProduct.mrp,
            weight: newProduct.weight, 
            length: newProduct.length, width: newProduct.width, height: newProduct.height,
            hsnCode: newProduct.hsnCode, skuId: newProduct.skuId, category: newProduct.category, 
            description: newProduct.description, washcare: newProduct.washcare, shippingPolicy: newProduct.shippingPolicy, 
            productImage: finalMainImage, sizeChart: finalSizeChart, images: finalExtraImages, 
            stock: JSON.stringify(newProduct.stock) 
        };

        const url = editId ? `/api/products/update/${editId}` : '/api/products/add';
        const method = editId ? 'PUT' : 'POST';

        const response = await fetch(url, {
            method: method, 
            headers: { "Content-Type": "application/json" }, 
            body: JSON.stringify(productData)
        });

        if (response.ok) {
            showToast(editId ? "✅ Product Updated Successfully!" : "✅ Product Added Successfully!");
            handleCancelEdit(); // Clears all states
            fetchData();
            setActiveTab('remove');
        } else { showToast("❌ Failed to save product."); }
    } catch (error) { 
        showToast("⚠️ Server Error during upload."); 
        console.error(error);
    }
  };

  const handleCancelEdit = () => {
      setNewProduct({ name: '', price: '', mrp: '', weight: '', length: '', width: '', height: '', hsnCode: '', skuId: '', description: '', washcare: '', shippingPolicy: '', category: 'tshirt', productImage: '', sizeChart: '', stock: { S: 0, M: 0, L: 0, XL: 0 } });
      setEditId(null);
      setExtraImages([]);
      setMainImageFile(null);
      setSizeChartFile(null);
      setExtraImageFiles([]);
      if(document.getElementById("mainImageInput")) document.getElementById("mainImageInput").value = "";
      if(document.getElementById("sizeChartInput")) document.getElementById("sizeChartInput").value = "";
      if(document.getElementById("extraImagesInput")) document.getElementById("extraImagesInput").value = "";
  };

  const handleRemoveProduct = (id) => {
      triggerConfirm("Delete Product?", "This product will be permanently removed.", async () => {
        try {
            await fetch(`/api/products/delete/${id}`, { method: 'DELETE' });
            setProducts(products.filter(p => p.id !== id)); 
            showToast("🗑️ Product Deleted");
        } catch (error) { showToast("❌ Error deleting product."); }
      });
  };

  const handleInputChange = (e) => setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
  const handleStockChange = (size, value) => setNewProduct({ ...newProduct, stock: { ...newProduct.stock, [size]: parseInt(value) || 0 } });
  
  // 🟢 UPDATED HANDLERS: Store Raw File and Create temporary preview URL
  const handleMainImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImageFile(file);
      setNewProduct({ ...newProduct, productImage: URL.createObjectURL(file) });
    }
  };

  const handleSizeChartUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSizeChartFile(file);
      setNewProduct({ ...newProduct, sizeChart: URL.createObjectURL(file) });
    }
  };

  const handleExtraImagesUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
        setExtraImageFiles(files);
        const previews = files.map(file => URL.createObjectURL(file));
        setExtraImages(previews);
    }
  };

  const getOrdersToDisplay = () => {
      if (activeTab === 'returns') return getReturnOrders();
      if (activeTab === 'archived') return getArchivedOrders();
      return getActiveOrders();
  };

  return (
    <div className="admin-container">
      
      {/* 🟢 CUSTOM CONFIRMATION POPUP */}
      {confirmModal.isOpen && (
        <div className="confirm-overlay">
            <div className="confirm-box">
                <h3>{confirmModal.title}</h3>
                <p>{confirmModal.message}</p>
                <div className="confirm-actions">
                    <button className="btn-confirm-no" onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}>
                        CANCEL
                    </button>
                    <button className="btn-confirm-yes" onClick={confirmModal.onConfirm}>
                        YES, CONFIRM
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
        <h1 className="section-title" style={{margin:0}}>ADMIN DASHBOARD</h1>
        <button onClick={fetchData} style={{background:'black', color:'white', padding:'10px 15px', border:'none', borderRadius:'5px', cursor:'pointer', fontSize:'0.8rem'}}>
            REFRESH DATA ↻
        </button>
      </div>
      
      {/* TABS NAVIGATION */}
      <div className="admin-tabs-container">
        <button className={`admin-tab ${activeTab === 'active_orders' ? 'active' : ''}`} onClick={() => setActiveTab('active_orders')}>
            Orders ({getActiveOrders().length})
        </button>
        
        <button className={`admin-tab ${activeTab === 'returns' ? 'active' : ''}`} onClick={() => setActiveTab('returns')} style={{color: getReturnOrders().length > 0 ? '#d32f2f' : 'inherit'}}>
            ⚠ Returns ({getReturnOrders().length})
        </button>

        <button className={`admin-tab ${activeTab === 'archived' ? 'active' : ''}`} onClick={() => setActiveTab('archived')}>
            History
        </button>
        
        <div style={{width:'1px', background:'#ccc', margin:'0 10px'}}></div> 
        
        <button className={`admin-tab ${activeTab === 'add' ? 'active' : ''}`} onClick={() => { handleCancelEdit(); setActiveTab('add'); }}>
            {editId ? "Edit Product" : "Product +"}
        </button>
        <button className={`admin-tab ${activeTab === 'remove' ? 'active' : ''}`} onClick={() => setActiveTab('remove')}>Products</button>
        
        <button className={`admin-tab ${activeTab === 'carousel' ? 'active' : ''}`} onClick={() => setActiveTab('carousel')} style={{background: '#ff9800', color:'white', marginLeft:'10px'}}>
            Banners
        </button>
        
        <button className={`admin-tab ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')} style={{background: '#9c27b0', color:'white', marginLeft:'10px'}}>
            Reviews ({reviews.length})
        </button>
      </div>

      <div className="admin-content-card">
        
        {/* --- 🟢 CAROUSEL TAB --- */}
        {activeTab === 'carousel' && (
            <div style={{padding:'10px'}}>
                <h3 style={{marginBottom:'15px'}}>Dynamic Homepage Banners</h3>
                
                <div style={{background:'#f9f9f9', padding:'20px', borderRadius:'8px', border:'1px solid #eee', marginBottom:'30px'}}>
                    <div style={{display:'flex', gap:'15px', alignItems:'center', flexWrap:'wrap'}}>
                        <div style={{display:'flex', flexDirection:'column', gap:'5px'}}>
                            <label style={{fontSize:'0.8rem', fontWeight:'bold'}}>Select Banner Type:</label>
                            <select value={carouselType} onChange={(e) => setCarouselType(e.target.value)} className="modern-input" style={{padding:'8px', width:'150px', cursor:'pointer'}}>
                                <option value="DESKTOP">DESKTOP</option>
                                <option value="MOBILE">MOBILE</option>
                            </select>
                        </div>
                        
                        <div style={{display:'flex', flexDirection:'column', gap:'5px', flex:1}}>
                            <label style={{fontSize:'0.8rem', fontWeight:'bold'}}>Choose Image(s):</label>
                            <div style={{display: 'flex', gap: '10px'}}>
                                <input 
                                    id="bannerInput" 
                                    type="file" 
                                    multiple 
                                    onChange={(e) => setBannerFiles(Array.from(e.target.files))} 
                                    className="modern-input" 
                                    style={{flex: 1}} 
                                />
                                <button 
                                    onClick={handleBannerUploadSubmit} 
                                    style={{background:'black', color:'white', border:'none', padding:'0 20px', borderRadius:'4px', cursor:'pointer', fontWeight:'bold', letterSpacing:'1px'}}
                                >
                                    UPLOAD
                                </button>
                            </div>
                        </div>
                    </div>
                    <p style={{fontSize:'0.75rem', color:'#666', marginTop:'10px'}}>
                        * Limits: <b>Desktop (Max 3)</b> | <b>Mobile (Max 4)</b>. You can select multiple files at once.
                    </p>
                </div>

                {/* Desktop Grid */}
                <h4 style={{borderBottom:'2px solid black', display:'inline-block', marginBottom:'15px', paddingBottom:'5px'}}>
                    Desktop Banners ({banners.filter(b=>b.type==='DESKTOP').length}/3)
                </h4>
                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(250px, 1fr))', gap:'15px', marginBottom:'40px'}}>
                    {banners.filter(b => b.type === 'DESKTOP').map(b => (
                        <div key={b.id} style={{position:'relative', borderRadius:'8px', overflow:'hidden', border:'1px solid #ddd', boxShadow:'0 2px 5px rgba(0,0,0,0.1)'}}>
                            <img src={b.imageUrl || b.image} alt="desktop" style={{width:'100%', height:'120px', objectFit:'cover', display:'block'}} />
                            <button onClick={() => deleteBanner(b.id)} style={{position:'absolute', top:5, right:5, background:'#d32f2f', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'bold'}}>DELETE 🗑️</button>
                        </div>
                    ))}
                    {banners.filter(b => b.type === 'DESKTOP').length === 0 && <p style={{color:'#999', fontSize:'0.9rem', fontStyle:'italic'}}>No desktop banners uploaded.</p>}
                </div>

                {/* Mobile Grid */}
                <h4 style={{borderBottom:'2px solid black', display:'inline-block', marginBottom:'15px', paddingBottom:'5px'}}>
                    Mobile Banners ({banners.filter(b=>b.type==='MOBILE').length}/4)
                </h4>
                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'15px'}}>
                    {banners.filter(b => b.type === 'MOBILE').map(b => (
                        <div key={b.id} style={{position:'relative', borderRadius:'8px', overflow:'hidden', border:'1px solid #ddd', boxShadow:'0 2px 5px rgba(0,0,0,0.1)'}}>
                            <img src={b.imageUrl || b.image} alt="mobile" style={{width:'100%', height:'200px', objectFit:'cover', display:'block'}} />
                            <button onClick={() => deleteBanner(b.id)} style={{position:'absolute', top:5, right:5, background:'#d32f2f', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'bold'}}>DELETE 🗑️</button>
                        </div>
                    ))}
                    {banners.filter(b => b.type === 'MOBILE').length === 0 && <p style={{color:'#999', fontSize:'0.9rem', fontStyle:'italic'}}>No mobile banners uploaded.</p>}
                </div>
            </div>
        )}

        {/* --- REVIEWS TAB --- */}
        {activeTab === 'reviews' && (
             <div className="table-responsive">
                <table className="order-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Product</th>
                            <th>User</th>
                            <th>Rating</th>
                            <th>Comment</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reviews.length === 0 ? 
                            <tr><td colSpan="6" style={{textAlign:'center', padding:'20px'}}>No Reviews Yet.</td></tr> :
                            reviews.map((r) => (
                                <tr key={r.id}>
                                    <td>#{r.id}</td>
                                    <td>
                                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                            {r.productImage && <img src={getAdminImageUrl(r.productImage)} alt="" style={{width:'40px', height:'40px', objectFit:'cover', borderRadius:'4px'}}/>}
                                            <span style={{fontSize:'0.9rem', fontWeight:'bold'}}>{r.productName}</span>
                                        </div>
                                    </td>
                                    <td>{r.user}</td>
                                    <td>
                                        <span style={{color:'gold', fontSize:'1.2rem'}}>
                                            {"★".repeat(r.rating)}
                                            <span style={{color:'#ccc'}}>{"★".repeat(5-r.rating)}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <p style={{margin:0, fontSize:'0.9rem'}}>{r.comment}</p>
                                        <div style={{display:'flex', gap:'5px', marginTop:'5px'}}>
                                            {[r.image1, r.image2, r.image3, r.image4].map((img, idx) => (
                                                img && <img key={idx} src={`data:image/jpeg;base64,${img}`} alt="review-img" style={{width:'30px', height:'30px', objectFit:'cover', border:'1px solid #eee'}} />
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <button 
                                            onClick={() => handleDeleteReview(r.id)}
                                            style={{backgroundColor:'#c62828', color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontSize:'0.8rem'}}
                                        >
                                            DELETE 🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
             </div>
        )}

        {/* --- ORDERS TABLES --- */}
        {['active_orders', 'returns', 'archived'].includes(activeTab) && (
          <div className="table-responsive">
            <table className="order-table">
              <thead>
                  <tr>
                      <th>ID</th>
                      <th style={{width: '25%'}}>Customer</th>
                      <th>Items</th>
                      <th>Amount</th>
                      <th>Status & AWB</th>
                      <th>Action</th>
                  </tr>
              </thead>
              <tbody>
                {loading ? (
                    <tr><td colSpan="6" style={{textAlign:'center', padding:'30px'}}>Loading...</td></tr>
                ) : getOrdersToDisplay().length === 0 ? (
                    <tr><td colSpan="6" style={{textAlign:'center', padding:'30px', color:'#999'}}>No orders in this category.</td></tr>
                ) : (
                    getOrdersToDisplay().map((order) => (
                    <tr key={order.id}>
                        <td style={{fontWeight:'bold'}}>#{order.id}</td>
                        <td>
                            <div style={{fontWeight:'bold'}}>{order.username}</div>
                            <div style={{fontSize:'0.8rem', color:'#555'}}>{order.phone}</div>
                            <div style={{fontSize:'0.75rem', color:'#888'}}>{order.address}</div>
                        </td>
                        <td>
                             <div style={{maxWidth:'200px', fontSize:'0.85rem'}}>
                                {(() => {
                                    try {
                                            const items = JSON.parse(order.orderItems);
                                            return items.map((i, idx) => (
                                                <div key={idx} style={{borderBottom:'1px dashed #eee', marginBottom:'2px'}}>
                                                    <b>{i.qty}x</b> {i.name} ({i.size})
                                                </div>
                                            ));
                                    } catch(e) { return "Error"; }
                                })()}
                             </div>
                        </td>
                        <td>
                            <div style={{fontWeight:'bold'}}>Rs. {order.totalAmount}</div>
                            <span style={{fontSize:'0.7rem', padding:'2px 4px', background:'#eee', borderRadius:'3px'}}>{order.paymentMode}</span>
                        </td>
                        <td>
                            <span className={`status-badge status-${getDisplayStatus(order.status).toLowerCase().split(' ')[0]}`}>
                                {getDisplayStatus(order.status)}
                            </span>
                            {order.awbNumber && (
                                <div style={{marginTop:'8px', fontSize:'0.8rem', color:'#1565c0', fontWeight:'bold', background:'#e3f2fd', padding:'4px', borderRadius:'4px', border:'1px solid #90caf9'}}>
                                    AWB: {order.awbNumber}
                                </div>
                            )}
                        </td>
                        <td>
                            {activeTab === 'active_orders' && (
                                <div style={{display:'flex', flexDirection:'column', gap:'5px'}}>
                                    <select className="status-select" value={order.status} onChange={(e) => handleStatusChange(order.id, e.target.value)} style={{marginBottom:'5px'}}>
                                            <option value="Pending">Pending</option>
                                            <option value="Shipped">Shipped</option>
                                            <option value="Out for Delivery">Out for Delivery</option>
                                            <option value="Delivered">Delivered</option>
                                            <option value="Cancelled">Cancelled</option>
                                    </select>
                                    <button onClick={() => handleArchive(order)} style={{background:'#555', color:'white', border:'none', padding:'6px', borderRadius:'4px', cursor:'pointer', fontSize:'0.75rem'}}>🗄️ Archive</button>
                                </div>
                            )}
                            {activeTab === 'returns' && (
                                <div style={{display:'flex', gap:'5px', flexDirection:'column'}}>
                                    <button onClick={() => handleStatusChange(order.id, 'Return Approved')} style={{background:'#2e7d32', color:'white', border:'none', padding:'6px', borderRadius:'4px', cursor:'pointer', fontSize:'0.8rem'}}>✅ Approve</button>
                                    <button onClick={() => handleStatusChange(order.id, 'Delivered')} style={{background:'#c62828', color:'white', border:'none', padding:'6px', borderRadius:'4px', cursor:'pointer', fontSize:'0.8rem'}}>❌ Reject</button>
                                </div>
                            )}
                            {activeTab === 'archived' && (
                                <div style={{display:'flex', gap:'5px', flexDirection:'column'}}>
                                    <button onClick={() => handleRestore(order)} style={{background:'#fff', border:'1px solid #999', color:'#555', padding:'5px', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'bold'}}>↺ Restore</button>
                                    <button onClick={() => handleDeleteOrder(order.id)} style={{background:'#c62828', color:'white', border:'none', padding:'5px', borderRadius:'4px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'bold'}}>🗑️ Delete</button>
                                </div>
                            )}
                        </td>
                    </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* --- ADD / EDIT PRODUCT FORM --- */}
        {activeTab === 'add' && (
          <form className="add-product-form" onSubmit={handleProductSubmit}>
            {editId && (
                <div style={{background:'#e3f2fd', padding:'10px', borderRadius:'5px', marginBottom:'20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <span style={{fontWeight:'bold', color:'#1565c0'}}>📝 Editing Mode</span>
                    <button type="button" onClick={handleCancelEdit} style={{background:'transparent', border:'1px solid #1565c0', color:'#1565c0', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>Cancel</button>
                </div>
            )}
            <div className="form-grid">
                <div className="form-left">
                    <div className="form-group"><label>Product Name</label><input type="text" name="name" value={newProduct.name} onChange={handleInputChange} required /></div>
                    <div className="form-group"><label>Stock (Per Size)</label><div style={{display:'flex', gap:'10px'}}>{['S', 'M', 'L', 'XL'].map(size => (<div key={size} style={{flex:1}}><span style={{fontSize:'0.8rem', fontWeight:'bold'}}>{size}</span><input type="number" min="0" value={newProduct.stock[size]} onChange={(e) => handleStockChange(size, e.target.value)} style={{marginTop:'5px', padding:'8px'}}/></div>))}</div></div>
                    
                    <div className="form-row">
                        <div className="form-group half"><label>Selling Price</label><input type="text" name="price" value={newProduct.price} onChange={handleInputChange} required /></div>
                        <div className="form-group half"><label>MRP</label><input type="number" name="mrp" value={newProduct.mrp} onChange={handleInputChange} /></div>
                        <div className="form-group half"><label>Weight</label><input type="text" name="weight" value={newProduct.weight} onChange={handleInputChange} placeholder="e.g. 500g" /></div>
                    </div>

                    {/* 🟢 NEW: PARCEL DIMENSIONS */}
                    <div className="form-row" style={{ display: 'flex', gap: '10px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Length (cm)</label>
                            <input type="number" name="length" value={newProduct.length} onChange={handleInputChange} placeholder="e.g. 10" />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Width (cm)</label>
                            <input type="number" name="width" value={newProduct.width} onChange={handleInputChange} placeholder="e.g. 10" />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Height (cm)</label>
                            <input type="number" name="height" value={newProduct.height} onChange={handleInputChange} placeholder="e.g. 5" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group half">
                            <label>SKU ID</label>
                            <input type="text" name="skuId" value={newProduct.skuId} onChange={handleInputChange} placeholder="e.g. TSH-BLK-01" />
                        </div>
                        <div className="form-group half">
                            <label>HSN Code</label>
                            <input type="text" name="hsnCode" value={newProduct.hsnCode} onChange={handleInputChange} placeholder="e.g. 6109" />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Category</label>
                        <select name="category" value={newProduct.category} onChange={handleInputChange}>
                            <option value="tshirt">T-Shirt</option>
                            <option value="shirt">Shirt</option>
                            <option value="jeans">Jeans</option>
                            <option value="Polo-Tshirt">Polo-Tshirt</option>
                            <option value="lower">Lower</option>
                        </select>
                    </div>
                    <div className="form-group"><label>Description</label><textarea name="description" value={newProduct.description} onChange={handleInputChange} rows="4"></textarea></div>
                    
                    <div className="form-group"><label>Washcare Instructions</label><textarea name="washcare" value={newProduct.washcare} onChange={handleInputChange} rows="3" placeholder="e.g. Machine wash cold..."></textarea></div>
                    <div className="form-group"><label>Shipping Policy</label><textarea name="shippingPolicy" value={newProduct.shippingPolicy} onChange={handleInputChange} rows="3" placeholder="e.g. Ships within 24 hours..."></textarea></div>

                </div>
                <div className="form-right">
                    <div className="form-group upload-box">
                        <label>Main Image</label>
                        {newProduct.productImage && <img src={getAdminImageUrl(newProduct.productImage)} alt="Preview" style={{width:'100px', height:'120px', objectFit:'cover', display:'block', margin:'10px auto'}} />}
                        <input id="mainImageInput" type="file" onChange={handleMainImageUpload} />
                    </div>
                    <div className="form-group upload-box">
                        <label>Gallery Images</label>
                        <input id="extraImagesInput" type="file" multiple onChange={handleExtraImagesUpload} />
                        {extraImages.length > 0 && <span className="success-badge" style={{display:'block', marginTop:'5px'}}>✔ {extraImages.length} Images Added</span>}
                    </div>
                    
                    <div className="form-group upload-box">
                        <label>Size Chart Image (Optional)</label>
                        {newProduct.sizeChart && <img src={getAdminImageUrl(newProduct.sizeChart)} alt="Size Chart Preview" style={{width:'100px', height:'120px', objectFit:'cover', display:'block', margin:'10px auto'}} />}
                        <input id="sizeChartInput" type="file" onChange={handleSizeChartUpload} />
                    </div>

                    <button type="submit" className="btn-black full-width">{editId ? "UPDATE PRODUCT" : "ADD PRODUCT"}</button>
                </div>
            </div>
          </form>
        )}

        {/* --- PRODUCTS LIST --- */}
        {activeTab === 'remove' && (
            <div className="table-responsive">
            <table className="order-table">
              <thead><tr><th>ID</th><th>Image</th><th>Name</th><th>SKU / HSN</th><th>Weight / Dim.</th><th>Stock</th><th>Action</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan="7">Loading...</td></tr> :
                 products.length === 0 ? <tr><td colSpan="7">No Products.</td></tr> : 
                    products.map((p) => {
                        let stockDisplay = "N/A";
                        try { const s = JSON.parse(p.stock); stockDisplay = `S:${s.S} M:${s.M} L:${s.L} XL:${s.XL}`; } catch(e) {}
                        return (
                        <tr key={p.id}>
                            <td>#{p.id}</td>
                            <td><div className="table-img-wrapper">{p.productImage ? <img src={getAdminImageUrl(p.productImage)} alt={p.name} /> : "No Img"}</div></td>
                            <td>{p.name}</td>
                            <td>
                                <div style={{fontSize:'0.85rem', fontWeight:'bold'}}>{p.skuId || '-'}</div>
                                <div style={{fontSize:'0.75rem', color:'#666'}}>HSN: {p.hsnCode || '-'}</div>
                            </td>
                            <td>
                                <div style={{fontSize:'0.85rem', fontWeight:'bold'}}>{p.weight || '-'}</div>
                                <div style={{fontSize:'0.75rem', color:'#666'}}>{p.length||10}x{p.width||10}x{p.height||5} cm</div>
                            </td>
                            <td><div style={{fontSize:'0.8rem', color:'#555'}}>{stockDisplay}</div></td>
                            <td>
                                <div style={{display:'flex', gap:'10px'}}>
                                    <button onClick={() => handleEditClick(p)} style={{background:'#1976d2', color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontWeight:'bold', fontSize:'0.8rem'}}>EDIT</button>
                                    <button className="remove-btn" onClick={() => handleRemoveProduct(p.id)}>DELETE</button>
                                </div>
                            </td>
                        </tr>
                        );
                    })
                }
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;